/**
 * Scenes — пороги симпатии у шаблонов сцен.
 *
 * Раздел узкий намеренно: правится ОДНО число, `affinity_required`. Клипы,
 * граф переходов и остальной редактор сцен приедут на E10.
 *
 * Почему это число заслуживает раздела уже сейчас: им настраивается
 * развилка среза — «уровень пройден, симпатии не хватает, до сцены четыре
 * пункта». Порог 10 при прибавках 2/4/5/10/40 — это одна цифра, от которой
 * зависит, увидит ли игрок модалку вообще и какой путь выберет. Калибруется
 * она по живым данным, то есть из админки и без деплоя.
 *
 * Правка действует немедленно: статус сцены считается на каждый запрос
 * (`scenes/status.ts` на сервере), кэша у порога нет. Первое сохранение
 * забирает строку у сида — как в Economy, поэтому здесь тот же бейдж
 * владельца и та же кнопка «вернуть к файлу».
 */

import { useCallback, useEffect, useState } from 'react'
import { request } from '../api/client.js'
import type { ReclaimResult, SceneRow, ScenesResponse } from '../api/types.js'
import { AuditTrail } from '../components/AuditTrail.js'
import { DataTable, type Column } from '../components/DataTable.js'
import { PageHead } from '../components/Layout.js'
import { Card, Notice, SourceBadge, formatDate } from '../components/ui.js'

const COLUMNS: Column<SceneRow>[] = [
  { key: 'title', title: 'Scene', render: (row) => row.title },
  {
    key: 'place',
    title: 'Chapter · slot',
    render: (row) => (
      <span className="muted">
        {row.chapterTitle} · {row.slot}
      </span>
    ),
  },
  {
    key: 'threshold',
    title: 'Gate 💜',
    numeric: true,
    render: (row) => <strong>{row.affinityRequired}</strong>,
  },
  { key: 'source', title: 'Owner', render: (row) => <SourceBadge source={row.source} /> },
]

export function Scenes(): JSX.Element {
  const [rows, setRows] = useState<SceneRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await request<ScenesResponse>('/admin/scenes')
      setRows(data.scenes)
      setError(null)
      setSelected((current) => current ?? data.scenes[0]?.id ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, version])

  const current = rows?.find((row) => row.id === selected) ?? null

  return (
    <>
      <PageHead
        title="Scenes"
        subtitle="Affinity gates. The other half of the lock is clearing the level that carries the scene slot."
      />

      {error !== null && <Notice tone="error">{error}</Notice>}

      <div className="grid grid--2">
        <Card title="Scene templates" hint="The gate is how much 💜 the chapter assistant needs." padded={false}>
          <DataTable
            columns={COLUMNS}
            rows={rows ?? []}
            rowKey={(row) => row.id}
            loading={rows === null}
            empty="No scene templates — the database was never seeded."
            onRowClick={(row) => setSelected(row.id)}
          />
        </Card>

        <div className="stack">
          <Card
            title={current === null ? 'Gate' : `Gate: ${current.title}`}
            hint="Takes effect immediately — scene status is computed per request, the gate has no cache."
            actions={
              current === null ? undefined : (
                <>
                  <SourceBadge source={current.source} />
                  {current.source === 'admin' && (
                    <ReclaimButton scene={current} onDone={() => setVersion((v) => v + 1)} />
                  )}
                </>
              )
            }
          >
            {current === null ? (
              <p className="muted">Pick a scene on the left.</p>
            ) : (
              <ThresholdForm
                scene={current}
                onSaved={() => setVersion((v) => v + 1)}
              />
            )}
          </Card>

          {current !== null && (
            <Card title="Change log" hint="Every edit writes a row into admin_audit." padded={false}>
              <AuditTrail entity="scene_templates" entityId={current.id} refreshKey={version} />
            </Card>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Форма из одного поля. `EditForm` из Economy здесь не подходит: он строит
 * форму по форме jsonb-значения, а тут правится одна колонка строки.
 */
function ThresholdForm({ scene, onSaved }: { scene: SceneRow; onSaved: () => void }): JSX.Element {
  const [draft, setDraft] = useState(String(scene.affinityRequired))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Смена выбранной сцены обязана сбросить черновик: иначе число, набранное
  // для одной сцены, осталось бы в поле у другой.
  useEffect(() => {
    setDraft(String(scene.affinityRequired))
    setError(null)
    setSaved(false)
  }, [scene.id, scene.affinityRequired])

  const parsed = Number(draft)
  const valid = draft.trim() !== '' && Number.isInteger(parsed) && parsed >= 0 && parsed <= 999
  const changed = valid && parsed !== scene.affinityRequired

  const save = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await request(`/admin/scenes/${encodeURIComponent(scene.id)}`, {
        method: 'PUT',
        body: { affinity_required: parsed },
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack stack--sm">
      {scene.source === 'seed' ? (
        <Notice tone="warn">
          This row is owned by the seed: a deploy will overwrite the gate with the
          value from content/scene-template.json. The first save from here hands the
          scene over to the admin panel — “Reclaim by file” gives it back.
        </Notice>
      ) : (
        <Notice>
          This scene is detached from the file: deploys no longer touch its gate.
        </Notice>
      )}

      <label className="field">
        <span className="field__label">Affinity gate, 💜</span>
        <input
          className="input"
          type="number"
          min={0}
          max={999}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setSaved(false)
          }}
        />
        <span className="field__hint">
          The scene unlocks when affinity with the chapter assistant reaches this number
          AND the level carrying slot {scene.slot} is cleared.
        </span>
      </label>

      {!valid && draft.trim() !== '' && (
        <Notice tone="error">An integer between 0 and 999 is required.</Notice>
      )}
      {error !== null && <Notice tone="error">{error}</Notice>}
      {saved && <Notice tone="ok">Saved. Players see it immediately, with no restart.</Notice>}

      <div className="row">
        <button className="btn btn--primary" type="button" disabled={!changed || busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save the gate'}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          edited by: {scene.source === 'seed' ? 'the seed' : scene.updatedBy ?? '—'} · {formatDate(scene.updatedAt)}
        </span>
      </div>
    </div>
  )
}

/**
 * «Вернуть к файлу» — то же действие, что `pnpm seed --reclaim
 * scene_templates.<id>`, и та же функция на сервере. Двухшаговая по той же
 * причине, что в Economy: она стирает калибровку, подобранную по живым
 * данным, и откатить её нечем.
 */
function ReclaimButton({ scene, onDone }: { scene: SceneRow; onDone: () => void }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!open) {
    return (
      <button className="btn btn--sm" type="button" onClick={() => setOpen(true)}>
        Reclaim by file
      </button>
    )
  }

  const reclaim = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      await request<ReclaimResult>(`/admin/scenes/${encodeURIComponent(scene.id)}/reclaim`, {
        method: 'POST',
      })
      setOpen(false)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="stack stack--sm"
      style={{
        flexBasis: '100%',
        padding: 'var(--gap)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-2)',
      }}
    >
      <strong style={{ fontSize: 13 }}>Hand “{scene.title}” back to the seed?</strong>
      <p className="card__hint" style={{ margin: 0 }}>
        The gate becomes whatever content/scene-template.json says, right now.
        The database currently holds {scene.affinityRequired} 💜.
      </p>
      {error !== null && <Notice tone="error">{error}</Notice>}
      <div className="row">
        <button className="btn btn--sm" type="button" disabled={busy} onClick={() => void reclaim()}>
          {busy ? 'Reclaiming…' : 'Yes, hand it back'}
        </button>
        <button className="btn btn--ghost btn--sm" type="button" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  )
}
